import {
  type TransformationTemplate,
  type ExtractedRecord,
  type CoverageSchemaAnalysis,
  type ValidAny,
  TargetSchema,
  ResourceName,
} from "@/types";
import { getProps } from "@/utils/helpers/columnsHelper";
import { getRecordData, getUnusedData } from "./recordUtils";

export interface HeaderMapping {
  detectedHeader: string;
  targetFields: string[];
  sampleValue: ValidAny;
  isMapped: boolean;
}

/**
 * Map target schemas to their corresponding resource types for label lookup
 */
const SCHEMA_TO_RESOURCE_MAP: Record<TargetSchema, ResourceName> = {
  [TargetSchema.CLAIMANT]: ResourceName.CLAIMANTS,
  [TargetSchema.STOPLOSS_CLAIM]: ResourceName.STOPLOSS_CLAIMS,
  [TargetSchema.CLAIM_ITEM]: ResourceName.CLAIM_ITEMS,
  [TargetSchema.CLIENT]: ResourceName.CLIENTS,
  [TargetSchema.REIMBURSEMENT]: ResourceName.REIMBURSEMENTS,
  [TargetSchema.POLICY]: ResourceName.POLICIES,
};

/**
 * Build a reverse map from a template's field_mappings: source header name → target fields.
 *
 * Replaces the older target→source lookup. Inverting the relationship lets the
 * mapping dialog iterate the document's own detected headers as the source of
 * truth (per `processing_metadata.detected_headers`) and reverse-look up the
 * template's suggestion, instead of iterating template entries and showing
 * source strings the file may not contain.
 *
 * Multiple target fields can share one source header (e.g. a `claimant_name`
 * column also feeding `claimant_first_name`), so the value is an array.
 */
export const buildReverseHeaderMap = (
  template: TransformationTemplate | undefined,
  selectedSchema: CoverageSchemaAnalysis | null
): Record<string, string[]> => {
  if (!template?.field_mappings || !selectedSchema) return {};

  const map: Record<string, string[]> = {};
  for (const m of template.field_mappings) {
    if (m.target_schema !== selectedSchema.target_schema) continue;
    const key = m.extraction_location?.key;
    if (!key) continue;
    if (!map[key]) map[key] = [];
    map[key].push(m.target_field);
  }
  return map;
};

/**
 * Build the header-mapping rows shown in the Field Mapping Review modal.
 *
 * Contract: the document's `processing_metadata.detected_headers` is the
 * source of truth for the "Detected Header" column and for which rows render
 * at all. The template only contributes the suggested target fields ("Maps
 * To"). Sample values come from the document's own extracted data (mapped
 * branch) or `unused_data` (unmapped branch).
 *
 * @param extractedRecords - Extracted records for this schema
 * @param reverseHeaderMap - Source header → target fields (from
 *   `buildReverseHeaderMap`)
 * @param detectedHeaders - Headers found in the file
 *   (`processing_metadata.detected_headers`)
 * @param includeUnmapped - Emit rows for headers without a template mapping
 */
export const buildHeaderMappings = (
  extractedRecords: ExtractedRecord[],
  reverseHeaderMap: Record<string, string[]>,
  detectedHeaders: string[],
  includeUnmapped: boolean = false
): HeaderMapping[] => {
  const recordsData = extractedRecords.map((r) => ({
    data: getRecordData(r),
    unusedData: getUnusedData(r),
  }));

  const findSampleForField = (field: string): ValidAny => {
    for (const record of recordsData) {
      if (record.data && record.data[field] !== undefined && record.data[field] !== null && record.data[field] !== "") {
        return record.data[field];
      }
    }
    return undefined;
  };

  const findSampleForUnmappedHeader = (header: string): ValidAny => {
    for (const record of recordsData) {
      const val = findSampleValueForHeader(header, record.unusedData);
      if (val !== "—" && val !== undefined && val !== null && val !== "") {
        return val;
      }
    }
    return "—";
  };

  const results: HeaderMapping[] = [];
  const seen = new Set<string>();

  for (const header of detectedHeaders) {
    if (seen.has(header)) continue;
    seen.add(header);

    const targetFields = reverseHeaderMap[header] ?? [];
    const isMapped = targetFields.length > 0;

    if (!isMapped && !includeUnmapped) continue;

    const sampleValue = isMapped ? findSampleForField(targetFields[0]) : findSampleForUnmappedHeader(header);

    results.push({ detectedHeader: header, targetFields, sampleValue, isMapped });
  }

  return results;
};

/**
 * Helper to find sample value for a detected header in unused_data
 * Handles case-insensitive matching since unused_data keys may be normalized
 */
const findSampleValueForHeader = (header: string, unusedData: Record<string, ValidAny> | undefined): ValidAny => {
  if (!unusedData) return "—";

  // Try exact match first
  if (header in unusedData) {
    return unusedData[header];
  }

  // Try case-insensitive match
  const normalizedHeader = header.toLowerCase();
  const matchingKey = Object.keys(unusedData).find((key) => key.toLowerCase() === normalizedHeader);

  return matchingKey ? unusedData[matchingKey] : "—";
};

/**
 * Get a user-friendly label for a field from the resource metadata
 * @param fieldName - The technical field name
 * @param schema - The target schema type
 * @returns User-friendly label or the field name if not found
 */
export const getFieldLabel = (fieldName: string, schema: TargetSchema): string => {
  const recordType = SCHEMA_TO_RESOURCE_MAP[schema];
  if (!recordType) return fieldName;

  try {
    return getProps(fieldName, recordType).label;
  } catch {
    // Fallback to field name if metadata not found
    return fieldName;
  }
};

/**
 * Get the appropriate status chip configuration for a mapping
 * @param isMapped - Whether the header is mapped to a field
 * @returns Object with label and color for the status chip
 */
export const getMappingStatus = (isMapped: boolean): { label: string; color: "warning" | "default" } => {
  if (!isMapped) return { label: "Unmapped", color: "warning" };
  return { label: "Mapped", color: "default" };
};

/**
 * Build the reprocess payload for updating field mappings
 * @param editedMappingsBySchema - Mappings organized by schema
 * @param editedSchemas - Set of schemas that have been edited
 * @param clientId - The ID of the client (either selected or from the document) to send for reprocessing
 * @returns API payload for reprocess endpoint
 */
export const buildReprocessPayload = (
  allMappingsBySchema: Record<string, Record<string, string[]>>,
  schemasWithChanges: Set<string>,
  invalidSchemas: Set<string>,
  clientId: string
) => {
  const field_mappings: Record<string, Record<string, string[]>> = {};

  Object.entries(allMappingsBySchema).forEach(([schemaKey, schemaMappings]) => {
    // Skip edited schemas that failed validation
    if (schemasWithChanges.has(schemaKey) && invalidSchemas.has(schemaKey)) {
      return;
    }

    const filteredMappings = Object.entries(schemaMappings)
      .filter(([_, targetFields]) => targetFields.length > 0)
      .reduce(
        (acc, [sourceHeader, targetFields]) => {
          acc[sourceHeader] = targetFields;
          return acc;
        },
        {} as Record<string, string[]>
      );

    if (Object.keys(filteredMappings).length > 0) {
      field_mappings[schemaKey] = filteredMappings;
    }
  });

  // Reason should reflect the actual scope of the submission, not just the
  // edited subset. The payload includes every initialized schema with
  // non-empty mappings — backend overwrites all of them on save, so the
  // audit trail must show the full scope. Falls back to schemasWithChanges
  // count if no payload schemas remain (e.g. all-empty case).
  const submittedSchemaCount = Object.keys(field_mappings).length || schemasWithChanges.size;
  return {
    field_mappings,
    reason: `User updated field mappings for ${submittedSchemaCount} schema${submittedSchemaCount !== 1 ? "s" : ""} during review`,
    client_id_ref: clientId,
  };
};

/**
 * Configuration for save button text generation
 */
interface SaveButtonConfig {
  isSaving: boolean;
  validSchemas: string[];
  invalidSchemas: string[];
  schemasWithChanges: Set<string>;
}

/**
 * Get the appropriate text for the save button based on validation state
 * Used in FieldMappingDialog to display contextual save button text
 *
 * @param config - Current state of validation and saving
 * @returns Appropriate button text for the current state
 */
export const getSaveButtonText = ({
  isSaving,
  validSchemas,
  invalidSchemas,
  schemasWithChanges,
}: SaveButtonConfig): string => {
  if (isSaving) {
    return "Saving...";
  }

  // No edits yet: nothing to fix and nothing to save. validSchemas/invalidSchemas
  // are both derived from schemasWithChanges, so on initial open (zero edits) they
  // are empty — don't imply the user must "Fix Validation Errors" when they
  // haven't changed anything and no error is theirs to act on here (#6425). The
  // unknown/duplicate/missing reasons are surfaced by the blocking alert instead.
  if (schemasWithChanges.size === 0) {
    return "No Changes to Save";
  }

  // Changes exist but none are valid → the user genuinely has errors to fix.
  if (validSchemas.length === 0) {
    return "Fix Validation Errors";
  }

  if (invalidSchemas.length > 0) {
    const schemaWord = validSchemas.length === 1 ? "Schema" : "Schemas";
    return `Save ${validSchemas.length} Valid ${schemaWord} (${invalidSchemas.length} with errors)`;
  }

  const schemaWord = schemasWithChanges.size > 1 ? "schemas" : "schema";
  return `Save & Reprocess (${schemasWithChanges.size} ${schemaWord})`;
};

/**
 * Determine if the save button should be disabled
 *
 * @param isSaving - Whether a save operation is in progress
 * @param canSave - Whether the current state allows saving
 * @returns True if save button should be disabled
 */
export const isSaveDisabled = (isSaving: boolean, canSave: boolean): boolean => {
  return isSaving || !canSave;
};
